import './GuestAuthGate.scss';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SEO from '../SEO/SEO';
import useBrandNavigation from '../../hooks/useBrandNavigation';
import HeaderNavigation, { APP_HEADER_NAV_ITEMS, HeaderNavItem } from '../HeaderNavigation/HeaderNavigation';
import SideMenu from '../SideMenu/SideMenu';
import { useTheme } from '../../context/ThemeContext';

export type GuestAuthGateVariant = 'tasks' | 'projects';

const SEO_BY_VARIANT: Record<GuestAuthGateVariant, { title: string }> = {
    tasks: { title: 'Требуется вход — Задачи — CodeReadAI' },
    projects: { title: 'Требуется вход — Проекты — CodeReadAI' },
};

const GATE_LEAD =
    'Зарегистрируйтесь или войдите в аккаунт, чтобы продолжить';

interface GuestAuthGateProps {
    variant: GuestAuthGateVariant;
}

const GuestAuthGate: React.FC<GuestAuthGateProps> = ({ variant }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { title: seoTitle } = SEO_BY_VARIANT[variant];
    const { handleBrandClick } = useBrandNavigation();
    const { logoSrc } = useTheme();
    const [sideMenuOpen, setSideMenuOpen] = React.useState(false);

    const navItemActive = (item: HeaderNavItem, currentPath: string) => {
        if (item.path === '/tasks') {
            return currentPath === '/tasks' || currentPath.startsWith('/task/');
        }
        if (item.path === '/projects') {
            return currentPath === '/projects' || currentPath.startsWith('/project/');
        }
        return currentPath === item.path;
    };

    return (
        <>
            <SEO
                title={seoTitle}
                description={GATE_LEAD}
                url={location.pathname}
                type="website"
                noindex
            />
            <div className="guest-auth-gate-page">
                <header className="tasks-header" role="banner">
                    <div className="header-left">
                        <button
                            type="button"
                            className="app-hamburger-btn"
                            onClick={() => setSideMenuOpen(true)}
                            aria-label="Открыть меню разделов"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <line x1="3" y1="6" x2="21" y2="6" />
                                <line x1="3" y1="12" x2="21" y2="12" />
                                <line x1="3" y1="18" x2="21" y2="18" />
                            </svg>
                        </button>
                        <div className="brand-logo" aria-label="codereadai" onClick={handleBrandClick}>
                            <img src={logoSrc} alt="CodeReadAI" className="brand-icon" />
                            <span className="brand-text">codereadai</span>
                        </div>
                    </div>
                    <HeaderNavigation
                        items={APP_HEADER_NAV_ITEMS}
                        currentPath={location.pathname}
                        onNavigate={navigate}
                        isItemActive={navItemActive}
                    />
                    <div className="header-right">
                        <div className="chat-auth-actions">
                            <button
                                type="button"
                                className="chat-auth-link"
                                onClick={() => navigate('/login', { state: { from: location } })}
                            >
                                Войти
                            </button>
                            <button
                                type="button"
                                className="chat-auth-link chat-auth-link--primary"
                                onClick={() => navigate('/register', { state: { from: location } })}
                            >
                                Регистрация
                            </button>
                        </div>
                    </div>
                </header>

                <div className="guest-auth-gate-main">
                    <main className="sign-wrap">
                        <section className="sign-card" aria-label="Доступ к разделу">
                            <p className="guest-auth-gate-lead">{GATE_LEAD}</p>

                            <div className="actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => navigate('/login', { state: { from: location } })}
                                >
                                    Войти
                                </button>
                            </div>
                            <div className="actions">
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => navigate('/register', { state: { from: location } })}
                                >
                                    Зарегистрироваться
                                </button>
                            </div>
                        </section>
                    </main>
                </div>
                <SideMenu
                    isOpen={sideMenuOpen}
                    onClose={() => setSideMenuOpen(false)}
                    items={APP_HEADER_NAV_ITEMS}
                    currentPath={location.pathname}
                    onNavigate={navigate}
                    isItemActive={navItemActive}
                />
            </div>
        </>
    );
};

export default GuestAuthGate;
